import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import {
  getOrCreateChatRoomV2,
  sendMessageSecureV2,
  markChatAsReadV2,
  listChatRoomsByHost,
  listChatRoomsByGuest,
  listMessagesByChatRoom,
  onSendMessageSecure,
} from '../../graphql/messaging';
import { getCloudFrontImageUrl } from '../../util/cloudFrontUtils';
import { notifyErrorToast } from '../../util/notifyActionError';

// ============================================================================
// useGetOrCreateChatRoom
// ============================================================================
export const useGetOrCreateChatRoom = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [executeMutation] = useMutation(getOrCreateChatRoomV2);

  const execute = useCallback(async (studioID) => {
    setLoading(true);
    setError(null);

    try {
      const result = await executeMutation({
        variables: { input: { studioID } }
      });

      const chatRoom = result?.data?.getOrCreateChatRoom;
      if (!chatRoom) {
        throw new Error('Fehler beim Erstellen des Chats');
      }
      
      // Process studio images if they exist
      if (chatRoom.studio && chatRoom.studio.images && Array.isArray(chatRoom.studio.images)) {
        chatRoom.studio = {
          ...chatRoom.studio,
          images: chatRoom.studio.images.map((img) => {
            if (!img || typeof img !== 'string') return '';
            if (img.startsWith('http')) return img;
            try {
              return getCloudFrontImageUrl(img);
            } catch (error) {
              console.error('Error processing studio image:', error);
              return '';
            }
          })
        };
      }

      // Process profile pictures
      if (chatRoom.host?.profilePicture) {
        try {
          chatRoom.host.profilePicture = getCloudFrontImageUrl(chatRoom.host.profilePicture, {
            width: 200,
            height: 200,
            fit: 'cover'
          });
        } catch (error) {
          console.error('Error processing host profile picture:', error);
        }
      }

      if (chatRoom.guest?.profilePicture) {
        try {
          chatRoom.guest.profilePicture = getCloudFrontImageUrl(chatRoom.guest.profilePicture, {
            width: 200,
            height: 200,
            fit: 'cover'
          });
        } catch (error) {
          console.error('Error processing guest profile picture:', error);
        }
      }

      return chatRoom;
    } catch (err) {
      const message = err?.graphQLErrors?.[0]?.message || err?.message || 'Unbekannter Fehler';
      setError(message);
      console.error('[useGetOrCreateChatRoom] Error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [executeMutation]);

  return { execute, loading, error };
};

// ============================================================================
// useSendMessage
// ============================================================================
export const useSendMessage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [executeMutation] = useMutation(sendMessageSecureV2);

  const execute = useCallback(async (chatRoomID, content, type = 'TEXT') => {
    
    // Security: Only allow TEXT type from client
    if (type !== 'TEXT') {
      const errorMsg = 'Only TEXT message type is allowed from client';
      setError(errorMsg);
      console.error('[useSendMessage] Invalid message type:', type);
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await executeMutation({
        variables: { input: { chatRoomID, content, type } }
      });

      const message = result?.data?.sendMessageSecure;
      if (!message) {
        throw new Error('Fehler beim Senden der Nachricht');
      }

      return message;
    } catch (err) {
      const message = err?.graphQLErrors?.[0]?.message || err?.message || 'Unbekannter Fehler';
      setError(message);
      console.error('[useSendMessage] Error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [executeMutation]);

  return { execute, loading, error };
};

// ============================================================================
// useMarkChatAsRead
// ============================================================================
export const useMarkChatAsRead = () => {
  const [executeMutation] = useMutation(markChatAsReadV2);

  const execute = useCallback(async (chatRoomID) => {
    try {
      const result = await executeMutation({
        variables: { input: { chatRoomID } }
      });

      return result?.data?.markChatAsRead?.success === true;
    } catch (err) {
      console.error('[useMarkChatAsRead] Error:', err);
      return false;
    }
  }, [executeMutation]);

  return { execute };
};

// ============================================================================
// useChatRooms - Alle Chats des Users
// ============================================================================
export const useChatRoomsV2 = () => {
  const { user } = useSelector((state) => state.auth);
  const userId = user?.id || user?._id || localStorage.getItem('userId');
  
  const [chatRooms, setChatRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { data: hostData, loading: hostLoading, error: hostError, refetch: refetchHost } = useQuery(listChatRoomsByHost, {
    variables: { hostID: userId, limit: 100 },
    skip: !userId,
    fetchPolicy: 'network-only',
    errorPolicy: 'all'
  });

  const { data: guestData, loading: guestLoading, error: guestError, refetch: refetchGuest } = useQuery(listChatRoomsByGuest, {
    variables: { guestID: userId, limit: 100 },
    skip: !userId,
    fetchPolicy: 'network-only',
    errorPolicy: 'all'
  });

  useEffect(() => {
    
    if (!userId) {
      setLoading(false);
      setChatRooms([]);
      return;
    }

    setLoading(hostLoading || guestLoading);
    
    if (hostError || guestError) {
      const errorMsg = hostError?.message || guestError?.message || 'Fehler beim Laden der Chats';
      setError(errorMsg);
      console.error('[useChatRoomsV2] Error:', hostError || guestError);
      setLoading(false);
      return;
    }

    if (!hostData && !guestData) {
      return;
    }

    try {
      const hostChats = hostData?.chatRoomsByHostID?.items || [];
      const guestChats = guestData?.chatRoomsByGuestID?.items || [];

      // Merge und dedupliziere
      const allChats = [...hostChats, ...guestChats];
      const uniqueChatsMap = new Map();
      allChats.forEach(chat => {
        if (chat && chat.id) {
          uniqueChatsMap.set(chat.id, chat);
        }
      });
      const uniqueChats = Array.from(uniqueChatsMap.values());

      // Process images and profile pictures
      const processedChats = uniqueChats.map(chat => {
        const processed = { ...chat };

        // Process studio images
        if (processed.studio?.images && Array.isArray(processed.studio.images)) {
          processed.studio = {
            ...processed.studio,
            images: processed.studio.images.map((img) => {
              if (!img || typeof img !== 'string') return '';
              if (img.startsWith('http')) return img;
              try {
                return getCloudFrontImageUrl(img);
              } catch (error) {
                console.error('Error processing studio image:', error);
                return '';
              }
            })
          };
        }

        // Process studio image (single)
        if (processed.studio?.image && !processed.studio.image.startsWith('http')) {
          try {
            processed.studio.image = getCloudFrontImageUrl(processed.studio.image);
          } catch (error) {
            console.error('Error processing studio image:', error);
          }
        }

        // Process host profile picture
        if (processed.host?.profilePicture) {
          try {
            processed.host.profilePicture = getCloudFrontImageUrl(processed.host.profilePicture, {
              width: 200,
              height: 200,
              fit: 'cover'
            });
          } catch (error) {
            console.error('Error processing host profile picture:', error);
          }
        }

        // Process guest profile picture
        if (processed.guest?.profilePicture) {
          try {
            processed.guest.profilePicture = getCloudFrontImageUrl(processed.guest.profilePicture, {
              width: 200,
              height: 200,
              fit: 'cover'
            });
          } catch (error) {
            console.error('Error processing guest profile picture:', error);
          }
        }

        return processed;
      });

      // Sortiere nach lastMessageAt (neueste zuerst)
      processedChats.sort((a, b) => {
        const dateA = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
        const dateB = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
        return dateB - dateA;
      });

      setChatRooms(processedChats);
    } catch (err) {
      const message = err?.message || 'Fehler beim Laden der Chats';
      setError(message);
      console.error('[useChatRoomsV2] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, hostData, guestData, hostLoading, guestLoading, hostError, guestError]);

  // Total unread count using new unreadCounts array with fallback to legacy fields
  const totalUnreadCount = chatRooms.reduce((total, chat) => {
    if (!userId) return total;
    
    // Use new unreadCounts array if available
    if (chat.unreadCounts && Array.isArray(chat.unreadCounts) && chat.unreadCounts.length > 0) {
      const userUnreadEntry = chat.unreadCounts.find(
        (entry) => entry && entry.userID === userId
      );
      if (userUnreadEntry && typeof userUnreadEntry.count === 'number') {
        return total + userUnreadEntry.count;
      }
    }
    
    // Fallback to legacy fields
    const isHost = chat.hostID === userId;
    const unread = isHost ? chat.hostUnreadCount : chat.guestUnreadCount;
    return total + (unread || 0);
  }, 0);

  const refetch = useCallback(() => {
    // Trigger refetch of both queries
    if (refetchHost) refetchHost();
    if (refetchGuest) refetchGuest();
  }, [refetchHost, refetchGuest]);

  // Refetch on window focus for system messages and chat list updates
  // System messages from automation Lambdas are written directly to DynamoDB
  // and don't trigger subscriptions, so we refetch when window gains focus
  useEffect(() => {
    if (!userId) return;
    
    const handleFocus = () => {
      if (refetchHost) refetchHost();
      if (refetchGuest) refetchGuest();
    };
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        if (refetchHost) refetchHost();
        if (refetchGuest) refetchGuest();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userId, refetchHost, refetchGuest]);

  return {
    chatRooms,
    loading,
    error,
    refetch,
    totalUnreadCount
  };
};

// ============================================================================
// useMessages - Messages mit Real-time Subscription
// ============================================================================
export const useMessagesV2 = (chatRoomID) => {
  const [messages, setMessages] = useState([]);
  // WICHTIG: loading ist false wenn kein chatRoomID (neuer Chat)
  const [loading, setLoading] = useState(!!chatRoomID);
  const [error, setError] = useState(null);
  const subscriptionRef = useRef(null);
  
  const { data, loading: queryLoading, error: queryError, subscribeToMore, refetch: refetchQuery } = useQuery(listMessagesByChatRoom, {
    variables: {
      chatRoomID,
      sortDirection: 'ASC',
      limit: 100
    },
    skip: !chatRoomID,
    fetchPolicy: 'network-only',
    errorPolicy: 'all'
  });

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    
    if (!chatRoomID) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (data?.messagesByChatRoomIDAndCreatedAt?.items) {
        const items = data.messagesByChatRoomIDAndCreatedAt.items;
        setMessages(items);
      }
    } catch (err) {
      const message = err?.graphQLErrors?.[0]?.message || err?.message || 'Fehler beim Laden der Nachrichten';
      setError(message);
      console.error('[useMessagesV2] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [chatRoomID, data]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Subscribe to new messages
  useEffect(() => {
    if (!chatRoomID) {
      // Kein chatRoomID = neuer Chat, loading ist false
      setLoading(false);
      return;
    }

    if (!subscribeToMore) {
      return;
    }
    
    try {
      
      const unsubscribe = subscribeToMore({
        document: onSendMessageSecure,
        variables: { 
          chatRoomID: chatRoomID  // Optional filter für Performance
        },
        updateQuery: (prev, { subscriptionData }) => {
          
          if (!subscriptionData.data) {
            return prev;
          }

          const newMessage = subscriptionData.data.onSendMessageSecure;
          if (!newMessage) {
            return prev;
          }

          
          // Prüfe ob die Message wirklich zu diesem ChatRoom gehört
          if (newMessage.chatRoomID !== chatRoomID) {
            return prev;
          }

          // Check if message already exists
          const existingItems = prev?.messagesByChatRoomIDAndCreatedAt?.items || [];
          const messageExists = existingItems.some(msg => msg.id === newMessage.id);
          
          if (messageExists) {
            return prev; // Duplikat
          }

          
          // Add new message
          return {
            ...prev,
            messagesByChatRoomIDAndCreatedAt: {
              ...prev.messagesByChatRoomIDAndCreatedAt,
              items: [...existingItems, newMessage]
            }
          };
        },
        onError: (err) => {
        }
      });

      subscriptionRef.current = unsubscribe;
    } catch (err) {
      notifyErrorToast('Fehler beim Einrichten der Nachrichtenabonnement. Bitte versuche es später erneut.', {
        title: 'Nachrichten',
      });
    }

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current();
        subscriptionRef.current = null;
      }
    };
  }, [chatRoomID, subscribeToMore]);

  const refetch = useCallback(() => {
    if (refetchQuery) {
      refetchQuery();
    }
  }, [refetchQuery]);

  // Refetch on window focus for system messages that might miss subscriptions
  // System messages from automation Lambdas are written directly to DynamoDB
  // and don't trigger subscriptions, so we refetch when window gains focus
  useEffect(() => {
    if (!chatRoomID) return;
    
    const handleFocus = () => {
      if (refetchQuery) {
        refetchQuery();
      }
    };
    
    const handleVisibilityChange = () => {
      if (!document.hidden && refetchQuery) {
        refetchQuery();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [chatRoomID, refetchQuery]);

  return {
    messages,
    loading: queryLoading,
    error: error || queryError,
    refetch
  };
};

